import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = (userId) => {
  const baseUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
  return `${baseUrl}/ws/kyc/${userId}`;
};

/**
 * Custom hook for KYC verification WebSocket connection
 * Provides real-time updates for verification progress and status
 */
export const useKYCWebSocket = (userId, enabled = true) => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [progress, setProgress] = useState(null); // { step, progress, step_labels }
  const [verificationResult, setVerificationResult] = useState(null); // Final verification result
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const connect = useCallback(() => {
    if (!userId || !enabled) return;

    try {
      setConnectionStatus('connecting');
      const wsUrl = getWebSocketUrl(userId);
      console.log('Connecting to KYC WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('KYC WebSocket connected');
        setConnectionStatus('connected');
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('KYC WebSocket message:', data);

          switch (data.type) {
            case 'connection_established':
              console.log('WebSocket connection confirmed');
              break;
            
            case 'kyc_progress':
              setProgress({
                step: data.step,
                progress: data.progress,
                stepLabels: data.step_labels,
                timestamp: data.timestamp
              });
              break;
            
            case 'kyc_status_update':
              setVerificationResult({
                status: data.status,
                verified: data.verified,
                overallScore: data.overall_score,
                message: data.message,
                rejectionReasons: data.rejection_reasons || [],
                timestamp: data.timestamp
              });
              // Reset progress when we get final result
              setProgress(null);
              break;
            
            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (error) {
          // Handle non-JSON messages (like pong)
          if (event.data !== 'pong') {
            console.log('Non-JSON WebSocket message:', event.data);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('KYC WebSocket error:', error);
        setConnectionStatus('error');
      };

      ws.onclose = (event) => {
        console.log('KYC WebSocket closed:', event.code, event.reason);
        setConnectionStatus('disconnected');
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 3 seconds if not intentionally closed
        if (enabled && event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connect();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [userId, enabled]);

  const disconnect = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    setConnectionStatus('disconnected');
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setProgress(null);
    setVerificationResult(null);
  }, []);

  // Connect when userId changes or enabled becomes true
  useEffect(() => {
    if (userId && enabled) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [userId, enabled, connect, disconnect]);

  return {
    connectionStatus,
    progress,
    verificationResult,
    connect,
    disconnect,
    reset,
    isConnected: connectionStatus === 'connected'
  };
};

export default useKYCWebSocket;
